import { useMemo } from 'react';
import { useEditorStore } from '../state/editor-store';
import { toSimulationGraphInput, readSimulationExtension } from './graph-bridge';
import { applyScenario } from '@/domain/simulation/scenarios/apply-scenario';
import { runMetricsEngine, type MetricsEngineResult } from '@/domain/simulation/selectors';
import { compareScenarios, type ScenarioComparisonColumn } from '@/domain/simulation/scenarios/compare-scenarios';
import { calculateReverseRequirements } from '@/domain/simulation/reverse/calculate-reverse';
import { evaluateBasicBottlenecks } from '@/domain/simulation/bottlenecks/evaluate-basic-bottlenecks';
import type { BasicBottleneckResult } from '@/domain/simulation/bottlenecks/contracts';
import { buildFinancialView } from '@/domain/simulation/financial/build-financial-view';
import type { FinancialScenarioView } from '@/domain/simulation/financial/contracts';
import type { ReverseResult, Scenario, SimulationGraphExtension } from '@/domain/simulation/contracts';

export interface SimulationViewModel {
  extension: SimulationGraphExtension;
  activeScenario: Scenario | null;
  base: MetricsEngineResult;
  active: MetricsEngineResult;
  comparison: { base: ScenarioComparisonColumn; scenarios: ScenarioComparisonColumn[] };
  reverse: ReverseResult | null;
  bottlenecks: BasicBottleneckResult;
  financial: FinancialScenarioView;
}

/**
 * SPEC-009 plan §36 — the single hook the Simulation UI reads from. Recomputes
 * from the graph reference (same pattern as SPEC-008's `useFunnelMetrics`):
 * every store `commit()` already produces a new graph object, so this stays
 * correct without a bespoke revision counter.
 */
export function useSimulation(): SimulationViewModel | null {
  const graph = useEditorStore((state) => state.graph);

  return useMemo(() => {
    if (!graph) return null;
    const extension = readSimulationExtension(graph);
    const graphInput = toSimulationGraphInput(graph);
    const base = runMetricsEngine(graphInput);

    const activeScenario = extension.scenarios.find((s) => s.id === extension.activeScenarioId) ?? null;
    const activeGraph = applyScenario(graphInput, activeScenario);
    const active = runMetricsEngine(activeGraph);

    const scenarioColumns = extension.scenarios.map((scenario) => ({
      scenarioId: scenario.id,
      scenarioName: scenario.name,
      result: runMetricsEngine(applyScenario(graphInput, scenario)),
    }));
    const comparison = compareScenarios(base, scenarioColumns);

    let reverse: ReverseResult | null = null;
    if (activeScenario?.target) {
      const edgeReverseShares: Record<string, number> = {};
      for (const [edgeId, override] of Object.entries(activeScenario.overrides.edgeOverrides)) {
        if (typeof override.reverseShare === 'number') edgeReverseShares[edgeId] = override.reverseShare;
      }
      reverse = calculateReverseRequirements(activeGraph, active, activeScenario.target, edgeReverseShares);
    }

    const bottleneckNodes = graphInput.nodes.map((node) => {
      const result = active.propagation.nodeResults.get(node.id);
      const requirement = reverse?.requiredNodeMetrics.get(node.id)?.find((r) => r.metric === 'exits');
      return {
        nodeId: node.id,
        conversion: (result?.conversion.effectiveValue as number | null) ?? null,
        entries: (result?.entries.effectiveValue as number | null) ?? null,
        exits: (result?.exits.effectiveValue as number | null) ?? null,
        targetGap: requirement?.gap ?? null,
      };
    });
    const bottlenecks = evaluateBasicBottlenecks(bottleneckNodes, activeScenario ? activeScenario.name : 'Base');

    const financial = buildFinancialView(base.funnel, active.funnel, {
      scenarioId: activeScenario?.id,
      scenarioName: activeScenario ? activeScenario.name : 'Base',
    });

    return { extension, activeScenario, base, active, comparison, reverse, bottlenecks, financial };
  }, [graph]);
}
